import React, { useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const CameraCapture = ({ open, onOpenChange, onCapture, angleLabel }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState("user");
  const [starting, setStarting] = useState(false);

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = async (mode) => {
    setError(null);
    setStarting(true);
    stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      if (e.name === "NotAllowedError" || e.name === "SecurityError") {
        setError("Camera permission was denied. Please allow camera access in your browser settings, then try again.");
      } else if (e.name === "NotFoundError") {
        setError("No camera was found on this device. You can upload from your gallery instead.");
      } else {
        setError("Could not start the camera. You can upload from your gallery instead.");
      }
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (open) start(facing);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      stop();
      onCapture(file);
    }, "image/jpeg", 0.92);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) stop(); onOpenChange(v); }}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="camera-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Take your {angleLabel} photo</DialogTitle>
          <DialogDescription className="text-brand-slate">Line up with the guide, then capture.</DialogDescription>
        </DialogHeader>
        <div className="pt-2 space-y-3">
          <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-black">
            {error ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white/80">
                <Camera className="w-8 h-8 mb-3 opacity-60" />
                <p className="text-sm">{error}</p>
              </div>
            ) : (
              <>
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="camera-video" />
                {starting && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="camera-capture-btn"
              onClick={capture}
              disabled={!!error || starting}
              className="flex-1 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2 h-11"
            >
              <Camera className="w-4 h-4" /> Capture
            </Button>
            <Button
              data-testid="camera-flip-btn"
              onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
              variant="outline"
              className="rounded-full h-11 w-11 p-0 border-brand-border"
              title="Switch camera"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
